import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ToggleReactionDocument,
  ToggleReactionMutation,
  ToggleReactionMutationVariables,
  ReactionType,
  ReactionParent,
} from '../../../../generated/graphql';
import { communityQueryKeys } from '../constants';
import { toast } from 'react-hot-toast';
import { apolloClient } from '@client/api/graphql/client';
import {
  updateReactionOnEntity,
  updateReactionOnList,
} from '../utils/toggleReactionHelpers';

type UseToggleReactionPostOptions = {
  skipOptimistic?: boolean;
};

export const useToggleReactionPost = (
  communityId: number | string,
  options?: UseToggleReactionPostOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    { id: string },
    unknown,
    { type: ReactionType; postId: string },
    () => ReactionType
  >({
    // @ts-ignore
    mutationFn: ({ type, postId }) => {
      const variables: ToggleReactionMutationVariables = {
        communityId: Number(communityId),
        entityId: postId,
        parent: ReactionParent.Post,
        type,
      };

      return apolloClient.mutate<
        ToggleReactionMutation,
        ToggleReactionMutationVariables
      >({
        mutation: ToggleReactionDocument,
        variables,
      });
    },
    ...(options?.skipOptimistic
      ? {
          onError: () => {
            toast.error('Something went wrong');
          },
          onSuccess: async (_data, { postId }) => {
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: communityQueryKeys.posts(communityId),
              }),
              queryClient.invalidateQueries({
                queryKey: communityQueryKeys.post(communityId, postId),
              }),
              queryClient.invalidateQueries({
                queryKey: communityQueryKeys.pinnedPosts(communityId),
              }),
            ]);
          },
        }
      : {
          onMutate: async ({ type, postId }) => {
            await queryClient.cancelQueries({
              queryKey: communityQueryKeys.posts(communityId),
            });
            const previousEntities = queryClient.getQueryData(
              communityQueryKeys.posts(communityId),
            );
            const previousPost = queryClient.getQueryData(
              communityQueryKeys.post(communityId, postId),
            );

            updateReactionOnEntity({
              entityId: postId,
              queryClient,
              queryKey: communityQueryKeys.post(communityId, postId),
              type,
            });
            updateReactionOnList({
              entityId: postId,
              queryClient,
              queryKey: communityQueryKeys.posts(communityId),
              type,
            });
            updateReactionOnList({
              entityId: postId,
              queryClient,
              queryKey: communityQueryKeys.pinnedPosts(communityId),
              type,
            });

            return function rollback() {
              queryClient.setQueryData(
                communityQueryKeys.posts(communityId),
                () => previousEntities,
              );
              queryClient.setQueryData(
                communityQueryKeys.post(communityId, postId),
                () => previousPost,
              );

              return type;
            };
          },
          onSuccess: (data, { postId }, rollback) => {
            const type = rollback?.();
            if (type) {
              updateReactionOnList({
                entityId: postId,
                queryClient,
                queryKey: communityQueryKeys.posts(communityId),
                type,
              });
              updateReactionOnEntity({
                entityId: postId,
                queryClient,
                queryKey: communityQueryKeys.post(communityId, postId),
                type,
              });
              updateReactionOnList({
                entityId: postId,
                queryClient,
                queryKey: communityQueryKeys.pinnedPosts(communityId),
                type,
              });
            }
            // TODO: mixpanel should live here
            // MixpanelHelper.communityEvents.recordReactionAdded({
            //   reactionLocation: commentId ? 'Comment' : 'Post',
            // });
          },
          onError: (_error, _variables, rollback) => {
            rollback?.();
            toast.error('Something went wrong');
          },
        }),
  });
};
